import http, { request } from './http';

export interface ReviewKnowledgeBaseInput {
  id: string;
  name: string;
  description?: string;
}

export interface KnowledgeBaseRecommendation {
  id: string;
  name?: string;
  reason?: string;
}

export interface ShallowScanRequest {
  projectId?: number;
  documentPath: string;
  documentContent: string;
  knowledgeBases?: ReviewKnowledgeBaseInput[];
}

export interface ShallowScanResult {
  doc_type: '文档' | '图纸' | '规范' | 'BOM' | '测试报告' | '其他' | string;
  ipd_stage: '概念' | '计划' | '开发' | '验证' | '发布' | '未知' | string;
  review_categories: string[];
  rule_set_id?: string;
  rule_set_label?: string;
  core_purposes?: string[];
  review_report?: string;
  matched_knowledge_base_ids?: string[];
  knowledge_base_recommendations?: KnowledgeBaseRecommendation[];
}

export interface ShallowScanResponse {
  document_path: string;
  result: ShallowScanResult;
}

export async function shallowScanDocument(payload: ShallowScanRequest): Promise<ShallowScanResponse> {
  const res = await request(http.post('/v1/review/shallow-scan', payload));
  return res as ShallowScanResponse;
}

export interface DeepReviewRequest {
  projectId?: number;
  documentPath: string;
  documentTitle?: string;
  documentMarkdown: string;
  projectContext?: string;
  docType?: string;
  ruleSetId?: string;
  reviewCategories?: string[];
  corePurposes?: string[];
  knowledgeContext?: string;
  selectedKnowledgeBaseIds?: string[];
  selectedKnowledgeDocumentIds?: string[];
  selectedStandardsKnowledgeDocumentIds?: string[];
  standardsContext?: string;
  consistencyContext?: string;
  requirementsContext?: string;
  projectBackground?: string;
  projectGoals?: string;
  documentDirection?: string;
  conversationId?: string;
  tableStructuresSupplement?: string;
}

export interface DeepReviewFinding {
  id: string;
  category: string;
  /** 去重合并后的多种错误类型（与 category 一致时仅一项） */
  categories?: string[];
  anchor_text: string;
  paragraph_label?: string;
  suggested_replacement: string;
  rationale?: string;
  author?: string;
}

export interface DeepReviewResponse {
  /** AI 对话框展示的用户说明（与 assistant_reply 一致） */
  summary: string;
  assistant_reply?: string;
  findings: DeepReviewFinding[];
  review_status?: 'complete' | 'partial' | 'failed';
  failed_phases?: string[];
  thought: { steps?: Array<{ key?: string; icon?: string; title?: string; text?: string }> };
}

export async function deepReviewDocument(payload: DeepReviewRequest): Promise<DeepReviewResponse> {
  // 多审查点 × 大模型调用，可能超过默认 60s
  const res = await request(http.post('/v1/review/deep-review', payload, { timeout: 300_000 }));
  return res as DeepReviewResponse;
}

/** 文档初步分析结果（与 ProjectDocumentViewer 本地 reviewPrefs 对齐） */
export interface DocumentReviewPreliminarySettings {
  projectId: number;
  documentObjectKey: string;
  shallowReviewComplete: boolean;
  reviewMeta: unknown;
  selectedReviewCategories: string[];
  corePurposes: string[];
  docTypeForPreliminary: string;
  ruleSetId: string;
  selectedKnowledgeBaseIds: string[];
  selectedKnowledgeDocumentIds: string[];
  selectedStandardsKnowledgeDocumentIds?: string[];
  updatedAt?: string;
}

export async function getPreliminaryReviewSettings(
  projectId: number,
  documentObjectKey: string,
): Promise<DocumentReviewPreliminarySettings | null> {
  const res = await http.get<DocumentReviewPreliminarySettings>('/v1/review/preliminary-settings', {
    params: { projectId, documentObjectKey },
    validateStatus: (status) => status === 200 || status === 204,
  });
  if (res.status === 204 || !res.data) return null;
  return res.data;
}

export async function savePreliminaryReviewSettings(
  payload: DocumentReviewPreliminarySettings,
): Promise<DocumentReviewPreliminarySettings> {
  const res = await request(http.put('/v1/review/preliminary-settings', payload));
  return res as DocumentReviewPreliminarySettings;
}

/** 文档评审共享历史版本（项目内多用户可见） */
export interface DocumentReviewWorkspaceVersion {
  id: string;
  label: string;
  content: string;
  updatedAt: number;
  major: number;
  minor: number;
  displaySuffix?: string;
  displayFullLine?: string;
  updatedByUserId?: number;
}

export interface DocumentReviewWorkspace {
  saveMode?: 'newversion' | 'overwrite';
  targetVersionId?: string;
  projectId: number;
  documentObjectKey: string;
  revision: number;
  updatedByUserId?: number;
  updatedAt?: string;
  bumpMajorOnNextNewVersion?: boolean;
  versions: DocumentReviewWorkspaceVersion[];
}

export async function getDocumentReviewWorkspace(
  projectId: number,
  documentObjectKey: string,
): Promise<DocumentReviewWorkspace | null> {
  const res = await http.get<DocumentReviewWorkspace>('/v1/review/document-workspace', {
    params: { projectId, documentObjectKey },
    validateStatus: (status) => status === 200 || status === 204,
  });
  if (res.status === 204 || !res.data) return null;
  return res.data;
}

export async function saveDocumentReviewWorkspace(
  payload: DocumentReviewWorkspace,
): Promise<DocumentReviewWorkspace> {
  const res = await request(http.put('/v1/review/document-workspace', payload));
  return res as DocumentReviewWorkspace;
}
